import React from "react";
import { useLang } from "@/lib/i18n-context";

// Zones based on real spec provided by the organizer:
//  - Sun Green park (Shiokaze Park), 10,000 m²
//  - Main Stage 12m × 20m + 7 support booths (talent/guest/sound control)
//  - 30 Food booths (3.6 × 5m), close to pathway
//  - 40 Exhibition booths (3.6 × 3.6m) — 20 left + 20 right of central Foodcourt
//  - 1 central Foodcourt Booth (7 × 5m)
//  - 12 Embassy booths + Sub-stage in front
//  - 24 Co-Partner Sponsor booths (3 clusters × 8)
//  - 8 Service booths
//  - 8 Little Foodcourt booths (branded by Co-Partner sponsors)
//  - 1 Business Matching tent (combined 8 booths, closed)

const ZONE = (name, count, size, sub) => ({ name, count, size, sub });

const D = {
  main_stage: {
    en: { name: "MAIN STAGE", sub: "Music · Dance · Traditional performances" },
    id: { name: "PANGGUNG UTAMA", sub: "Musik · Tari · Pertunjukan tradisional" },
    ja: { name: "メインステージ", sub: "音楽・舞踊・伝統芸能" },
    size: "12 × 20 m",
  },
  stage_support: {
    en: { name: "STAGE SUPPORT BOOTHS", sub: "3 changing rooms · 3 guest star lounges · 1 sound & LED control" },
    id: { name: "BOOTH PENDUKUNG PANGGUNG", sub: "3 ruang ganti · 3 ruang tamu · 1 kontrol suara & LED" },
    ja: { name: "ステージサポートブース", sub: "更衣室3・ゲスト控室3・音響&LED制御1" },
    count: "7 booths",
    size: "2 × 3 m each",
  },
  sub_stage: {
    en: { name: "SUB-STAGE", sub: "Speech Contest · Cosplay Show" },
    id: { name: "PANGGUNG PENDAMPING", sub: "Kontes Pidato · Pertunjukan Cosplay" },
    ja: { name: "サブステージ", sub: "スピーチコンテスト・コスプレショー" },
    sub2: { en: "In front of Embassy booths", id: "Di depan booth Kedutaan", ja: "大使館ブース前" },
  },
  embassy: {
    en: { name: "INDONESIAN EMBASSY", sub: "Consular & citizen services" },
    id: { name: "KEDUTAAN INDONESIA (KBRI)", sub: "Layanan konsuler & kewarganegaraan" },
    ja: { name: "インドネシア大使館", sub: "領事・市民サービス" },
    count: "12 booths",
    size: "3.6 × 3.6 m each",
  },
  business_matching: {
    en: { name: "BUSINESS MATCHING TENT", sub: "Closed booth · Indonesia × Japan HR & investment" },
    id: { name: "TENDA BUSINESS MATCHING", sub: "Booth tertutup · SDM & investasi Indonesia × Jepang" },
    ja: { name: "ビジネスマッチング特設テント", sub: "個室ブース・インドネシア × 日本 人材・投資" },
    count: "1 combined tent (= 8 units)",
    size: "≈ 7.2 × 14.4 m",
  },
  foodcourt_center: {
    en: { name: "CENTRAL FOODCOURT", sub: "Large covered tent · seating area" },
    id: { name: "FOODCOURT PUSAT", sub: "Tenda besar tertutup · area tempat duduk" },
    ja: { name: "セントラルフードコート", sub: "大型テント・座席エリア" },
    count: "1 unit",
    size: "≈ 7 × 5 m",
  },
  exhibition_left: {
    en: { name: "EXHIBITION BOOTHS · LEFT", sub: "Left of central Foodcourt" },
    id: { name: "BOOTH PAMERAN · KIRI", sub: "Sebelah kiri Foodcourt pusat" },
    ja: { name: "展示ブース・左側", sub: "セントラルフードコートの左側" },
    count: "20 booths",
    size: "3.6 × 3.6 m each",
  },
  exhibition_right: {
    en: { name: "EXHIBITION BOOTHS · RIGHT", sub: "Right of central Foodcourt" },
    id: { name: "BOOTH PAMERAN · KANAN", sub: "Sebelah kanan Foodcourt pusat" },
    ja: { name: "展示ブース・右側", sub: "セントラルフードコートの右側" },
    count: "20 booths",
    size: "3.6 × 3.6 m each",
  },
  little_foodcourt: {
    en: { name: "LITTLE FOODCOURT BOOTHS", sub: "Branded by Co-Partner sponsors · 2 booths after every 5 exhibitions" },
    id: { name: "MINI-FOODCOURT", sub: "Bermerek Co-Partner · 2 booth setiap 5 booth pameran" },
    ja: { name: "ミニフードコート", sub: "コーパートナー協賛・展示ブース5つごとに2ブース" },
    count: "8 booths (2 × 4)",
    size: "3.6 × 3.6 m each",
  },
  co_partner: {
    en: { name: "CO-PARTNER SPONSOR", sub: "3 clusters × 8 booths each" },
    id: { name: "SPONSOR CO-PARTNER", sub: "3 klaster × 8 booth" },
    ja: { name: "コーパートナースポンサー", sub: "3クラスター × 各8ブース" },
    count: "24 booths",
    size: "3.6 × 3.6 m each",
  },
  service: {
    en: { name: "SERVICE BOOTHS", sub: "Info · Ticketing · Volunteer HQ · First Aid" },
    id: { name: "BOOTH LAYANAN", sub: "Info · Tiket · Markas Relawan · P3K" },
    ja: { name: "サービスブース", sub: "案内・チケット・ボランティア本部・救護" },
    count: "8 booths",
    size: "3.6 × 3.6 m each",
  },
  food: {
    en: { name: "FOOD BOOTHS", sub: "Close to walking path · gas, water, refrigerator provided" },
    id: { name: "BOOTH MAKANAN", sub: "Dekat jalur pengunjung · gas, air, kulkas tersedia" },
    ja: { name: "飲食ブース", sub: "動線沿い・ガス・水道・冷蔵庫完備" },
    count: "30 booths",
    size: "3.6 × 5 m each",
  },
};

const AMENITIES = [
  { key: "entrance", en: "ENTRANCE GATE",    id: "GERBANG MASUK",    ja: "入場ゲート" },
  { key: "exit",     en: "EXIT GATE",        id: "GERBANG KELUAR",   ja: "退場ゲート" },
  { key: "toilet_p", en: "PERMANENT TOILET", id: "TOILET PERMANEN",  ja: "常設トイレ" },
  { key: "toilet_t", en: "PORTABLE TOILET",  id: "TOILET PORTABLE",  ja: "仮設トイレ" },
  { key: "smoking",  en: "SMOKING AREA",     id: "AREA MEROKOK",     ja: "喫煙エリア" },
  { key: "rubbish",  en: "RUBBISH · GOMI",   id: "TITIK SAMPAH",     ja: "ゴミステーション" },
  { key: "organizer",en: "ORGANIZER AREA",   id: "AREA PANITIA",     ja: "運営本部エリア" },
  { key: "generator",en: "GENERATOR",        id: "GENERATOR LISTRIK",ja: "発電機" },
];

function Block({ zone, lang, wide = false, extraWide = false, tall = false, highlight = false }) {
  const l = zone[lang] || zone.en;
  return (
    <div
      className={`bg-white border-2 rounded-2xl p-4 md:p-5 flex flex-col justify-center ${
        highlight ? "border-brand-red" : "border-border"
      } ${extraWide ? "md:col-span-3" : wide ? "md:col-span-2" : ""} ${tall ? "md:row-span-2" : ""}`}
      data-testid={`zone-${zone.en.name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/-$/, "")}`}
    >
      <div className={`font-display font-bold text-sm md:text-base leading-tight ${highlight ? "text-brand-red" : "text-foreground"}`}>
        {l.name}
      </div>
      {l.sub && (
        <div className="text-xs text-muted-foreground mt-1.5 leading-snug">{l.sub}</div>
      )}
      {zone.sub2 && (
        <div className="text-[11px] italic text-brand-red mt-1">{zone.sub2[lang] || zone.sub2.en}</div>
      )}
      <div className="flex flex-wrap gap-1.5 mt-2">
        {zone.count && (
          <span className="text-[11px] font-mono uppercase tracking-wider bg-brand-canvas px-2 py-0.5 rounded">
            {zone.count}
          </span>
        )}
        {zone.size && (
          <span className="text-[11px] font-mono uppercase tracking-wider bg-brand-canvas px-2 py-0.5 rounded">
            {zone.size}
          </span>
        )}
      </div>
    </div>
  );
}

function AmenityBlock({ item, lang }) {
  return (
    <div className="bg-brand-canvas rounded-xl px-3 py-2.5 text-center" data-testid={`amenity-${item.key}`}>
      <div className="text-[11px] font-display font-bold uppercase tracking-wider leading-tight">
        {item[lang] || item.en}
      </div>
    </div>
  );
}

export default function MapPage() {
  const { t, lang } = useLang();

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-8 py-16">
      <div className="mb-8">
        <div className="text-xs uppercase tracking-widest text-brand-red font-semibold mb-3">Venue Layout · Sun Green Park (Shiokaze Park) · 10,000 m²</div>
        <h1 className="font-display text-4xl md:text-5xl font-bold">{t("map_title")}</h1>
        <p className="text-muted-foreground mt-3 max-w-2xl">{t("map_sub")}</p>
        <p className="text-xs text-muted-foreground mt-1">
          Shiokaze Park, Odaiba Tokyo · 12 – 13 December 2026
        </p>
      </div>

      {/* Actual venue photo */}
      <div className="mb-10 rounded-3xl overflow-hidden border border-border relative" data-testid="venue-photo">
        <img src="/shiokaze_park_aerial.jpg" alt="Shiokaze Park aerial view" className="w-full h-auto object-cover" />
        <div className="absolute top-4 left-4 md:top-6 md:left-6 bg-white/95 backdrop-blur px-4 py-2.5 rounded-full text-xs uppercase tracking-widest font-semibold shadow-md flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-brand-red animate-pulse" />
          Sun Green Park · Shiokaze Park, Odaiba Tokyo
        </div>
      </div>

      {/* Clean top-down schematic */}
      <div className="bg-brand-canvas/50 rounded-3xl p-6 md:p-10 border border-border" data-testid="venue-diagram">
        <div className="text-center mb-8">
          <div className="text-xs uppercase tracking-widest text-brand-red font-semibold">Top-Down Layout</div>
          <h2 className="font-display text-2xl md:text-3xl font-bold mt-1">Booth & Stage Arrangement</h2>
          <p className="text-xs text-muted-foreground mt-2">
            Bird&apos;s-eye schematic · not to scale · based on organizer&apos;s spec
          </p>
        </div>

        {/* North indicator */}
        <div className="flex justify-end mb-3">
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <span className="font-bold text-brand-red">↑ N</span>
            <span>(toward the sea)</span>
          </div>
        </div>

        {/* NORTH — Main Stage & support booths */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
          <Block zone={D.main_stage} lang={lang} wide highlight />
          <Block zone={D.stage_support} lang={lang} />
        </div>

        {/* Row — Embassy · Sub-stage · Business Matching */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
          <Block zone={D.embassy} lang={lang} />
          <Block zone={D.sub_stage} lang={lang} highlight />
          <Block zone={D.business_matching} lang={lang} highlight />
        </div>

        {/* Row — Exhibition · Central Foodcourt · Exhibition */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
          <Block zone={D.exhibition_left} lang={lang} />
          <Block zone={D.foodcourt_center} lang={lang} highlight />
          <Block zone={D.exhibition_right} lang={lang} />
        </div>

        {/* Row — Little Foodcourt (interspersed) */}
        <div className="grid grid-cols-1 gap-3 mb-3">
          <Block zone={D.little_foodcourt} lang={lang} wide extraWide />
        </div>

        {/* Row — Co-Partner Sponsor · Service */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
          <Block zone={D.co_partner} lang={lang} wide />
          <Block zone={D.service} lang={lang} />
        </div>

        {/* Row — Food booths (along pathway) */}
        <div className="grid grid-cols-1 gap-3 mb-6">
          <Block zone={D.food} lang={lang} wide extraWide highlight />
        </div>

        {/* Divider + amenities */}
        <div className="flex items-center gap-3 my-6">
          <div className="flex-1 h-px bg-border" />
          <div className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">Amenities & Support</div>
          <div className="flex-1 h-px bg-border" />
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
          {AMENITIES.map((a) => <AmenityBlock key={a.key} item={a} lang={lang} />)}
        </div>
      </div>

      {/* Summary strip */}
      <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white border border-border rounded-2xl p-5 text-center">
          <div className="font-display text-3xl font-bold text-brand-red">10,000</div>
          <div className="text-xs text-muted-foreground mt-1 uppercase tracking-widest">m² venue</div>
        </div>
        <div className="bg-white border border-border rounded-2xl p-5 text-center">
          <div className="font-display text-3xl font-bold text-brand-red">138</div>
          <div className="text-xs text-muted-foreground mt-1 uppercase tracking-widest">Total booth units</div>
        </div>
        <div className="bg-white border border-border rounded-2xl p-5 text-center">
          <div className="font-display text-3xl font-bold text-brand-red">2</div>
          <div className="text-xs text-muted-foreground mt-1 uppercase tracking-widest">Stages (main + sub)</div>
        </div>
        <div className="bg-white border border-border rounded-2xl p-5 text-center">
          <div className="font-display text-3xl font-bold text-brand-red">12×20</div>
          <div className="text-xs text-muted-foreground mt-1 uppercase tracking-widest">Main stage · m</div>
        </div>
      </div>

      <div className="mt-6 bg-white border border-border rounded-2xl p-5 md:p-6 text-xs text-muted-foreground leading-relaxed">
        <strong className="text-foreground">Note:</strong> Food booths are positioned close to walking paths for stable cooking-setup base.
        Exhibition booths flank the Central Foodcourt (20 left + 20 right) with 8 Little Foodcourt booths interspersed
        (2 booths after every 5 exhibition booths on both sides), branded by Co-Partner sponsors. The Business Matching
        tent is a single closed structure combining 8 booth units. The Sub-Stage sits in front of the Embassy row and
        hosts the Speech Contest and Cosplay Show.
      </div>
    </div>
  );
}
